import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout as AntLayout,
  Menu,
  Avatar,
  Dropdown,
  Space,
  Typography,
  theme,
  Drawer,
  Button,
} from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  CarOutlined,
  ToolOutlined,
  TeamOutlined,
  ShoppingOutlined,
  FileTextOutlined,
  BarChartOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UsergroupAddOutlined,
  ClockCircleOutlined,
  TruckOutlined,
  BankOutlined,
  SafetyOutlined,
  AppstoreOutlined,
  CreditCardOutlined,
  DollarOutlined,
  FileProtectOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import Notificaciones from './Notificaciones';

const { Header, Sider, Content } = AntLayout;
const { Text } = Typography;

const Layout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  // Detectar si es móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setCollapsed(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Menú completo para administradores
  const allMenuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/clientes',
      icon: <UserOutlined />,
      label: 'Clientes',
    },
    {
      key: '/vehiculos',
      icon: <CarOutlined />,
      label: 'Vehículos',
    },
    {
      key: '/servicios',
      icon: <ToolOutlined />,
      label: 'Servicios',
    },
    {
      key: '/personal',
      icon: <UsergroupAddOutlined />,
      label: 'Personal',
    },
    {
      key: '/asistencias',
      icon: <ClockCircleOutlined />,
      label: 'Asistencias',
    },
    {
      key: '/mis-servicios',
      icon: <ToolOutlined />,
      label: 'Mis Servicios',
    },
    {
      key: '/mi-caja-chica',
      icon: <DollarOutlined />,
      label: 'Mi Caja Chica',
    },
    {
      key: '/unidades',
      icon: <TruckOutlined />,
      label: 'Unidades/Grúas',
    },
    {
      key: '/caja-chica',
      icon: <DollarOutlined />,
      label: 'Gestión Caja Chica',
    },
    {
      key: '/empresas',
      icon: <BankOutlined />,
      label: 'Empresas',
    },
    {
      key: '/aseguradoras',
      icon: <SafetyOutlined />,
      label: 'Aseguradoras',
    },
    {
      key: '/tipos-servicio',
      icon: <AppstoreOutlined />,
      label: 'Tipos de Servicio',
    },
    {
      key: '/metodos-pago',
      icon: <CreditCardOutlined />,
      label: 'Métodos de Pago',
    },
    {
      key: '/tarifas-base',
      icon: <DollarOutlined />,
      label: 'Tarifas Base',
    },
    {
      key: '/usuarios',
      icon: <UserOutlined />,
      label: 'Usuarios',
    },
    {
      key: '/inventario',
      icon: <ShoppingOutlined />,
      label: 'Inventario',
    },
    {
      key: '/gestion-documental',
      icon: <FileTextOutlined />,
      label: 'Gestión Documental',
    },
    // {
    //   key: '/facturacion',
    //   icon: <FileTextOutlined />,
    //   label: 'Facturación',
    // },
    {
      key: '/liquidacion-pagos',
      icon: <FileProtectOutlined />,
      label: 'Liquidación de Pagos',
    },
    {
      key: '/reportes',
      icon: <BarChartOutlined />,
      label: 'Reportes',
    },
  ];

  // Menú limitado para operadores
  const operatorMenuItems = [
    {
      key: '/mis-servicios',
      icon: <ToolOutlined />,
      label: 'Mis Servicios',
    },
    {
      key: '/mi-caja-chica',
      icon: <DollarOutlined />,
      label: 'Mi Caja Chica',
    },
  ];

  // Filtrar menú según el rol del usuario
  let menuItems;
  if (user?.rol === 'operador') {
    menuItems = operatorMenuItems;
  } else if (user?.rol === 'admin') {
    // Para administradores, excluir "Mis Servicios" y "Mi Caja Chica"
    menuItems = allMenuItems.filter(
      item => item.key !== '/mis-servicios' && item.key !== '/mi-caja-chica'
    );
  } else {
    menuItems = allMenuItems;
  }

  const userMenuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Cerrar Sesión',
      onClick: () => {
        logout();
        navigate('/login');
      },
    },
  ];

  const menuContent = (
    <>
      <div
        style={{
          height: 64,
          margin: 16,
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 'bold',
          fontSize: collapsed && !isMobile ? 14 : 18,
        }}
      >
        {collapsed && !isMobile ? 'SAM' : 'Sistema Auxilio'}
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[location.pathname]}
        items={menuItems}
        onClick={({ key }) => {
          navigate(key);
          if (isMobile) {
            setMobileMenuVisible(false);
          }
        }}
      />
    </>
  );

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      {isMobile ? (
        <Drawer
          title="Menú"
          placement="left"
          onClose={() => setMobileMenuVisible(false)}
          open={mobileMenuVisible}
          bodyStyle={{ padding: 0 }}
          width={256}
        >
          {menuContent}
        </Drawer>
      ) : (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          theme="dark"
          breakpoint="lg"
          collapsedWidth={isMobile ? 0 : 80}
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            height: '100vh',
            overflow: 'auto',
            zIndex: 1000,
          }}
        >
          {menuContent}
        </Sider>
      )}
      <AntLayout style={{
        marginLeft: !isMobile ? (collapsed ? 80 : 200) : 0,
        transition: 'margin-left 0.2s',
      }}>
        <Header
          style={{
            padding: isMobile ? '0 16px' : '0 24px',
            background: colorBgContainer,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'fixed',
            top: 0,
            left: !isMobile ? (collapsed ? 80 : 200) : 0,
            right: 0,
            zIndex: 999,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            transition: 'left 0.2s',
          }}
        >
          <div
            style={{ fontSize: 18, cursor: 'pointer' }}
            onClick={() => {
              if (isMobile) {
                setMobileMenuVisible(true);
              } else {
                setCollapsed(!collapsed);
              }
            }}
          >
            {isMobile ? <MenuUnfoldOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
          </div>
          <Space size={isMobile ? 'small' : 'large'} wrap>
            <Notificaciones />
            {!isMobile && <Text strong>{user?.nombre}</Text>}
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Avatar
                style={{ cursor: 'pointer' }}
                icon={<UserOutlined />}
              />
            </Dropdown>
          </Space>
        </Header>
        <Content
          style={{
            margin: isMobile ? '16px 8px' : '24px 16px',
            marginTop: isMobile ? '80px' : '88px',
            padding: isMobile ? 16 : 24,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: 8,
          }}
        >
          <Outlet />
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default Layout;

